<i18n src="./translations"></i18n>

<template>
  <div class="product-info-sections">
    <template v-if="isCarSelected">
      <PdpInfoAttributes
        v-if="carAttributes.length"
        :class="classes"
        :title="$t('product-info-sections.car-attributes')"
        :regular="carAttributes"
        :default-limit="carLimit"
        :is-slim="isSlim"
        is-filled-background
      />

      <PdpInfoAttributes
        v-if="information.length"
        :title="
          !isTitleHidden ? $t('product-info-sections.general-attributes') : ''
        "
        :regular="information"
        :default-limit="generalLimitWithCar"
        :is-slim="isSlim"
      />
    </template>

    <PdpInfoAttributes
      v-else-if="combinedAttributes.length"
      :title="
        !isTitleHidden ? $t('product-info-sections.general-attributes') : ''
      "
      :regular="combinedAttributes"
      :default-limit="carLimit"
      :is-slim="isSlim"
    />

    <PlpDescription21
      v-if="includeDescription && shouldShowDescription"
      :description="product.shortDescription"
      :class="classes"
    />

    <HazardousGood21
      v-if="showHazardousGoods"
      :information="product.hazardousGoodInformation"
    />

    <div v-if="includeMoreInfo">
      <PdpInfoOeLink
        v-if="showOeLink"
        :oe-numbers="variant === 'pdp' ? product.oeNumbers : []"
        :product-id="product.productId"
      />

      <PdpInfoCarLink v-if="showCarLink" :product-id="product.productId" />
    </div>
  </div>
</template>

<script>
import globalMixin from 'Libs/mixins/globalMixin';
import { mapState } from 'vuex';
import get from 'lodash/get';
import PdpInfoAttributes from './subcomponents/PdpInfoAttributes.vue';
import PdpInfoOeLink from './subcomponents/PdpInfoOeLink.vue';
import PdpInfoCarLink from './subcomponents/PdpInfoCarLink.vue';
import HazardousGood21 from 'Components/02-molecules/hazardous-good-21/HazardousGood21.vue';
import PlpDescription21 from 'Components/02-molecules/plp-description-21/PlpDescription21';
import { isTypeLabel } from 'Components/02-molecules/tyre-labels/models/tyre-labels';

export default {
  name: 'ProductInfoSections',
  components: {
    PdpInfoAttributes,
    PdpInfoOeLink,
    PdpInfoCarLink,
    HazardousGood21,
    PlpDescription21,
  },
  mixins: [globalMixin],
  props: {
    product: { type: Object, required: true },
    variant: { type: String, default: 'plp' }, // 'plp' | 'pdp'
    carLimit: { type: Number, default: 6 },
    includeDescription: { type: Boolean, default: true },
    includeMoreInfo: { type: Boolean, default: true },
    noCarPrepend: { type: Array, default: () => [] },
    isTitleHidden: { type: Boolean, default: false },
  },
  computed: {
    isSlim() {
      return !!(this.modifiersArray && this.modifiersArray.includes('slim'));
    },
    // OE link logic differs between variants:
    //  - PLP: backend only exposes a boolean aggregate (hasOeNumbers)
    //  - PDP: full oeNumbers array is loaded (may be empty until ajax)
    showOeLink() {
      if (this.variant === 'plp') return !!this.product?.hasOeNumbers;

      return (
        Array.isArray(this.product?.oeNumbers) &&
        this.product.oeNumbers.length > 0
      );
    },
    showCarLink() {
      return this.variant === 'plp' && !!this.product?.hasCarTypes;
    },
    ...mapState('core', {
      hazardousGoodEnabled: (state) =>
        get(state, 'salesChannel.config.hazardousGoodInfo', false),
    }),
    isCarSelected() {
      return this.product?.pdpCarBar?.key === 'SELECTED_CAR_OK';
    },
    attributes() {
      const list = (this.product && this.product.attributes) || [];
      const mapped = list.map((a) => ({
        label: a.title,
        info: a.value,
        isTop: a.isTop,
        id: a.id,
      }));
      return this.variant === 'plp'
        ? mapped.filter((a) => !isTypeLabel(a && a.id))
        : mapped;
    },
    carAttributes() {
      return this.attributes.filter(({ isTop }) => isTop);
    },
    information() {
      return this.attributes.filter(({ isTop }) => !isTop);
    },
    generalLimitWithCar() {
      return Math.max(
        Math.floor(this.carLimit / 2),
        this.carLimit - this.carAttributes.length
      );
    },
    combinedAttributes() {
      if (this.isCarSelected) return [];
      return [...this.noCarPrepend, ...this.carAttributes, ...this.information];
    },
    shouldShowDescription() {
      return (
        !!this.product?.shortDescription?.length && this.attributes.length === 0
      );
    },
    showHazardousGoods() {
      return (
        this.hazardousGoodEnabled &&
        !!this.product?.isHazardousGood &&
        this.product?.hazardousGoodInformation
      );
    },
    classes() {
      // TODO: deprecate legacy 'is-desktop' string usage after confirming no external deep selectors rely on it
      return { 'is-desktop': this.variant === 'pdp' };
    },
  },
};
</script>
